
/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
    Briefcase, Plus, Search, Filter,
    MoreVertical, Edit, Trash, Eye, X,
    Loader2, Check, ArrowLeft, Image as ImageIcon
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast, Toaster } from 'sonner';

const API_BASE = import.meta.env.VITE_API_BASE_URL_NEW;

export default function AdminServices() {
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        total: 0
    });

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
    const [currentServiceId, setCurrentServiceId] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedService, setSelectedService] = useState(null);

    const [formData, setFormData] = useState({
        title: { en: '', ar: '' },
        short_description: { en: '', ar: '' },
        long_description: { en: '', ar: '' },
        benefits: [], // Array of { id: null, title: { en: '', ar: '' } }
        image: null
    });

    // Helper to reset form
    const resetForm = () => {
        setFormData({
            title: { en: '', ar: '' },
            short_description: { en: '', ar: '' },
            long_description: { en: '', ar: '' },
            benefits: [], // Array of { id: null, title: { en: '', ar: '' } }
            image: null
        });
        setCurrentServiceId(null);
    };

    const fetchServices = async (page = 1) => {
        try {
            setLoading(true);
            const token = localStorage.getItem('admin_token');
            const response = await axios.get(`${API_BASE}/admin/services`, {
                params: { page },
                headers: { Authorization: `Bearer ${token}` }
            });

            setServices(response.data.data || []);
            if (response.data.meta) {
                setPagination({
                    current_page: response.data.meta.current_page || 1,
                    last_page: response.data.meta.last_page || 1,
                    total: response.data.meta.total || 0
                });
            }
            setError(null);
        } catch (err) {
            console.error(err);
            setError('Failed to fetch services');
            toast.error(err.response?.data?.message || "Failed to load services");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchServices();
    }, []);

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this service?")) return;

        try {
            const token = localStorage.getItem('admin_token');
            await axios.delete(`${API_BASE}/admin/services/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Service deleted successfully");
            fetchServices(pagination.current_page);
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || "Failed to delete service");
        }
    };

    const openCreateModal = () => {
        resetForm();
        setModalMode('create');
        setIsModalOpen(true);
    };

    const openEditModal = async (service) => {
        const token = localStorage.getItem('admin_token');

        // Open modal immediately with available data
        setFormData({
            title: {
                en: service.title?.en || '',
                ar: service.title?.ar || ''
            },
            short_description: {
                en: service.short_description?.en || '',
                ar: service.short_description?.ar || ''
            },
            long_description: {
                en: service.long_description?.en || '',
                ar: service.long_description?.ar || ''
            },
            benefits: service.benefits ? service.benefits.map(b => ({
                id: b.id,
                title: {
                    en: b.title?.en || '',
                    ar: b.title?.ar || ''
                }
            })) : [],
            image: null // We don't pre-fill file input
        });
        setCurrentServiceId(service.id);
        setModalMode('edit');
        setIsModalOpen(true);

        // Try to fetch full details from API in the background
        try {
            const response = await axios.get(`${API_BASE}/admin/services/${service.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            const serviceData = response.data.data || response.data;

            // Update form with full details if fetch succeeds
            setFormData({
                title: {
                    en: serviceData.title?.en || '',
                    ar: serviceData.title?.ar || ''
                },
                short_description: {
                    en: serviceData.short_description?.en || '',
                    ar: serviceData.short_description?.ar || ''
                },
                long_description: {
                    en: serviceData.long_description?.en || '',
                    ar: serviceData.long_description?.ar || ''
                },
                benefits: serviceData.benefits ? serviceData.benefits.map(b => ({
                    id: b.id,
                    title: {
                        en: b.title?.en || '',
                        ar: b.title?.ar || ''
                    }
                })) : [],
                image: null
            });
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || "Failed to load full service details, using cached data");
        }
    };

    const handleViewDetails = async (id) => {
        try {
            const token = localStorage.getItem('admin_token');
            const response = await axios.get(`${API_BASE}/admin/services/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            // Handle unwrapping if necessary (response.data.data or just response.data)
            setSelectedService(response.data.data || response.data);
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || "Failed to fetch service details");
        }
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        const token = localStorage.getItem('admin_token');

        try {
            // Prepare JSON body according to API structure
            const requestBody = {
                title: {
                    en: formData.title.en,
                    ar: formData.title.ar
                },
                short_description: {
                    en: formData.short_description.en,
                    ar: formData.short_description.ar
                },
                long_description: {
                    en: formData.long_description.en,
                    ar: formData.long_description.ar
                },
                benefits: formData.benefits.map(benefit => ({
                    title: {
                        en: benefit.title.en,
                        ar: benefit.title.ar
                    }
                }))
            };

            // If image exists, use FormData, otherwise use JSON
            if (formData.image) {
                const data = new FormData();
                data.append('title[en]', formData.title.en);
                data.append('title[ar]', formData.title.ar);
                data.append('short_description[en]', formData.short_description.en);
                data.append('short_description[ar]', formData.short_description.ar);
                data.append('long_description[en]', formData.long_description.en);
                data.append('long_description[ar]', formData.long_description.ar);

                // Append benefits as JSON string
                formData.benefits.forEach((benefit, index) => {
                    data.append(`benefits[${index}][title][en]`, benefit.title.en);
                    data.append(`benefits[${index}][title][ar]`, benefit.title.ar);
                });

                data.append('service_image', formData.image);

                if (modalMode === 'create') {
                    await axios.post(`${API_BASE}/admin/services`, data, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'multipart/form-data'
                        }
                    });
                    toast.success("Service created successfully");
                } else {
                    data.append('_method', 'PUT');
                    await axios.post(`${API_BASE}/admin/services/${currentServiceId}`, data, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'multipart/form-data'
                        }
                    });
                    toast.success("Service updated successfully");
                }
            } else {
                // No image, use JSON
                if (modalMode === 'create') {
                    await axios.post(`${API_BASE}/admin/services`, requestBody, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    });
                    toast.success("Service created successfully");
                } else {
                    // Add _method: PUT for backend to recognize as PUT request
                    const updateBody = {
                        ...requestBody,
                        _method: 'PUT'
                    };
                    await axios.post(`${API_BASE}/admin/services/${currentServiceId}`, updateBody, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    });
                    toast.success("Service updated successfully");
                }
            }

            setIsModalOpen(false);
            fetchServices(pagination.current_page);
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || "Operation failed");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Benefits Management
    const addBenefit = () => {
        setFormData(prev => ({
            ...prev,
            benefits: [...prev.benefits, { id: null, title: { en: '', ar: '' } }]
        }));
    };

    const removeBenefit = (index) => {
        setFormData(prev => ({
            ...prev,
            benefits: prev.benefits.filter((_, i) => i !== index)
        }));
    };

    const updateBenefit = (index, lang, value) => {
        const newBenefits = [...formData.benefits];
        newBenefits[index].title[lang] = value;
        setFormData(prev => ({ ...prev, benefits: newBenefits }));
    };

    return (
        <div className="relative">
            <Toaster position="top-right" richColors closeButton />

            {/* Header */}
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Services</h1>
                    <p className="text-gray-600 mt-2">Manage and organize your service offerings</p>
                </div>
                <button
                    onClick={openCreateModal}
                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-xl hover:from-blue-700 hover:to-blue-800 transition-colors shadow-lg shadow-blue-200"
                >
                    <Plus className="w-5 h-5" />
                    <span>Add Service</span>
                </button>
            </div>

            {/* List View */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden min-h-[400px]">
                {loading ? (
                    <div className="flex flex-col items-center justify-center p-20">
                        <Loader2 className="w-10 h-10 animate-spin text-blue-600 mb-4" />
                        <p className="text-gray-500">Loading services...</p>
                    </div>
                ) : services.length === 0 ? (
                    <div className="flex flex-col items-center justify-center p-20 text-center">
                        <Briefcase className="w-16 h-16 text-gray-300 mb-4" />
                        <h3 className="text-xl font-semibold text-gray-800 mb-2">No Services Found</h3>
                        <p className="text-gray-500 mb-6">Get started by creating your first service.</p>
                        <button
                            onClick={openCreateModal}
                            className="text-blue-600 font-medium hover:underline"
                        >
                            Create New Service
                        </button>
                    </div>
                ) : (
                    <>
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-100">
                                <tr>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Image</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Service Name (EN)</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Title (AR)</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Created At</th>
                                    <th className="px-6 py-4 text-right text-sm font-semibold text-gray-600">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {services.map((service) => (
                                    <motion.tr
                                        key={service.id}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="hover:bg-gray-50 transition-colors"
                                    >
                                        <td className="px-6 py-4">
                                            {service.service_image ? (
                                                <img
                                                    src={service.service_image.startsWith('http') ? service.service_image : `${import.meta.env.VITE_API_BASE_URL_NEW.replace('/api/v1', '')}/storage/${service.service_image}`}
                                                    alt={service.title?.en}
                                                    className="w-12 h-12 object-cover rounded-lg border border-gray-200"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.nextElementSibling.style.display = 'flex';
                                                    }}
                                                />
                                            ) : null}
                                            <div className={`w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 ${service.service_image ? 'hidden' : ''}`}>
                                                <ImageIcon className="w-6 h-6" />
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 font-medium text-gray-900">
                                            {service.title?.en || "N/A"}
                                        </td>
                                        <td className="px-6 py-4 text-gray-600 font-arabic">
                                            {service.title?.ar || "N/A"}
                                        </td>
                                        <td className="px-6 py-4 text-gray-500 text-sm">
                                            {new Date(service.created_at).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => handleViewDetails(service.id)}
                                                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                    title="View Details"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => openEditModal(service)}
                                                    className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                                    title="Edit"
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(service.id)}
                                                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                    title="Delete"
                                                >
                                                    <Trash className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </motion.tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Pagination */}
                        {pagination.last_page > 1 && (
                            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-between items-center text-sm text-gray-500">
                                <span>Showing page {pagination.current_page} of {pagination.last_page}</span>
                                <div className="flex gap-2">
                                    <button
                                        disabled={pagination.current_page === 1}
                                        onClick={() => fetchServices(pagination.current_page - 1)}
                                        className="px-3 py-1 border border-gray-200 rounded hover:bg-white disabled:opacity-50"
                                    >
                                        Previous
                                    </button>
                                    <button
                                        disabled={pagination.current_page === pagination.last_page}
                                        onClick={() => fetchServices(pagination.current_page + 1)}
                                        className="px-3 py-1 border border-gray-200 rounded hover:bg-white disabled:opacity-50"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Modal */}
            <AnimatePresence>
                {isModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsModalOpen(false)}
                            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                        />

                        <motion.div
                            initial={{ scale: 0.95, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 20 }}
                            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
                        >
                            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center z-10">
                                <h2 className="text-2xl font-bold text-gray-900">
                                    {modalMode === 'create' ? 'Create New Service' : 'Edit Service'}
                                </h2>
                                <button
                                    onClick={() => setIsModalOpen(false)}
                                    className="p-2 hover:bg-gray-100 rounded-full transition"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>

                            <div className="p-8">
                                <form onSubmit={handleFormSubmit} className="space-y-8">

                                    {/* Titles */}
                                    <div className="grid md:grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                            <label className="text-sm font-semibold text-gray-700">Title (English)</label>
                                            <input
                                                required
                                                type="text"
                                                value={formData.title.en}
                                                onChange={(e) => setFormData({ ...formData, title: { ...formData.title, en: e.target.value } })}
                                                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                                                placeholder="Service Name"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-sm font-semibold text-gray-700">Title (Arabic)</label>
                                            <input
                                                required
                                                type="text"
                                                dir="rtl"
                                                value={formData.title.ar}
                                                onChange={(e) => setFormData({ ...formData, title: { ...formData.title, ar: e.target.value } })}
                                                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none font-arabic"
                                                placeholder="اسم الخدمة"
                                            />
                                        </div>
                                    </div>

                                    {/* Short Descriptions */}
                                    <div className="grid md:grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                            <label className="text-sm font-semibold text-gray-700">Short Description (English)</label>
                                            <textarea
                                                required
                                                rows={3}
                                                value={formData.short_description.en}
                                                onChange={(e) => setFormData({ ...formData, short_description: { ...formData.short_description, en: e.target.value } })}
                                                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-sm font-semibold text-gray-700">Short Description (Arabic)</label>
                                            <textarea
                                                required
                                                rows={3}
                                                dir="rtl"
                                                value={formData.short_description.ar}
                                                onChange={(e) => setFormData({ ...formData, short_description: { ...formData.short_description, ar: e.target.value } })}
                                                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none font-arabic"
                                            />
                                        </div>
                                    </div>

                                    {/* Long Descriptions */}
                                    <div className="grid md:grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                            <label className="text-sm font-semibold text-gray-700">Long Description (English)</label>
                                            <textarea
                                                required
                                                rows={6}
                                                value={formData.long_description.en}
                                                onChange={(e) => setFormData({ ...formData, long_description: { ...formData.long_description, en: e.target.value } })}
                                                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-sm font-semibold text-gray-700">Long Description (Arabic)</label>
                                            <textarea
                                                required
                                                rows={6}
                                                dir="rtl"
                                                value={formData.long_description.ar}
                                                onChange={(e) => setFormData({ ...formData, long_description: { ...formData.long_description, ar: e.target.value } })}
                                                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none font-arabic"
                                            />
                                        </div>
                                    </div>

                                    {/* Benefits Section */}
                                    <div className="space-y-4 border-t pt-6">
                                        <div className="flex justify-between items-center">
                                            <label className="text-lg font-bold text-gray-800">Benefits</label>
                                            <button
                                                type="button"
                                                onClick={addBenefit}
                                                className="text-sm text-blue-600 font-semibold hover:bg-blue-50 px-3 py-1 rounded transition"
                                            >
                                                + Add Benefit
                                            </button>
                                        </div>

                                        <div className="space-y-4">
                                            {formData.benefits.map((benefit, index) => (
                                                <div key={index} className="flex gap-4 items-start bg-gray-50 p-4 rounded-xl relative group">
                                                    <div className="flex-1 space-y-3">
                                                        <input
                                                            type="text"
                                                            placeholder="Benefit in English"
                                                            value={benefit.title.en}
                                                            onChange={(e) => updateBenefit(index, 'en', e.target.value)}
                                                            className="w-full px-3 py-2 rounded border border-gray-300 text-sm"
                                                        />
                                                        <input
                                                            type="text"
                                                            dir="rtl"
                                                            placeholder="Benefit in Arabic"
                                                            value={benefit.title.ar}
                                                            onChange={(e) => updateBenefit(index, 'ar', e.target.value)}
                                                            className="w-full px-3 py-2 rounded border border-gray-300 text-sm font-arabic"
                                                        />
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeBenefit(index)}
                                                        className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                                                    >
                                                        <Trash className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            ))}
                                            {formData.benefits.length === 0 && (
                                                <div className="text-center py-6 text-gray-500 italic bg-gray-50 rounded-xl border border-dashed">
                                                    No benefits added yet.
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Image Upload */}
                                    <div className="space-y-2 pt-4 border-t">
                                        <label className="text-sm font-semibold text-gray-700">Service Image</label>
                                        <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center transition hover:border-blue-400 hover:bg-blue-50">
                                            <input
                                                type="file"
                                                id="image-upload"
                                                accept="image/*"
                                                className="hidden"
                                                onChange={(e) => setFormData({ ...formData, image: e.target.files[0] })}
                                            />
                                            <label htmlFor="image-upload" className="cursor-pointer flex flex-col items-center">
                                                {formData.image ? (
                                                    <div className="flex items-center gap-2 text-green-600">
                                                        <Check className="w-6 h-6" />
                                                        <span className="font-medium">{formData.image.name}</span>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <ImageIcon className="w-10 h-10 text-gray-400 mb-2" />
                                                        <span className="text-gray-600">Click to upload image</span>
                                                        <span className="text-xs text-gray-400 mt-1">PNG, JPG up to 5MB</span>
                                                    </>
                                                )}
                                            </label>
                                        </div>
                                    </div>

                                    {/* Footer Actions */}
                                    <div className="flex justify-end gap-4 pt-6 border-t bg-white sticky bottom-0">
                                        <button
                                            type="button"
                                            onClick={() => setIsModalOpen(false)}
                                            className="px-6 py-2.5 text-gray-600 font-medium hover:bg-gray-100 rounded-xl transition"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-xl hover:from-blue-700 hover:to-blue-800 transition shadow-lg shadow-blue-200 disabled:opacity-70 flex items-center gap-2"
                                        >
                                            {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                                            {modalMode === 'create' ? 'Create Service' : 'Update Service'}
                                        </button>
                                    </div>

                                </form>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Details Modal */}
            <AnimatePresence>
                {selectedService && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setSelectedService(null)}
                            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                        />
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 20 }}
                            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
                        >
                            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center z-10">
                                <h2 className="text-2xl font-bold text-gray-900">Service Details</h2>
                                <button
                                    onClick={() => setSelectedService(null)}
                                    className="p-2 hover:bg-gray-100 rounded-full transition"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>

                            <div className="p-6 space-y-8">
                                {/* Image and Basic Info */}
                                <div className="flex flex-col md:flex-row gap-6">
                                    <div className="flex-shrink-0">
                                        {selectedService.service_image ? (
                                            <img
                                                src={selectedService.service_image.startsWith('http') ? selectedService.service_image : `${import.meta.env.VITE_API_BASE_URL_NEW.replace('/api/v1', '')}/storage/${selectedService.service_image}`}
                                                alt={selectedService.title?.en}
                                                className="w-full md:w-48 h-48 object-cover rounded-xl shadow-md border"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.nextElementSibling.style.display = 'flex';
                                                }}
                                            />
                                        ) : null}
                                        <div className={`w-full md:w-48 h-48 bg-gray-100 rounded-xl flex items-center justify-center text-gray-400 border ${selectedService.service_image ? 'hidden' : ''}`}>
                                            <ImageIcon className="w-12 h-12" />
                                        </div>
                                    </div>
                                    <div className="flex-1 space-y-4">
                                        <div>
                                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">ID</label>
                                            <p className="text-gray-900 font-mono">#{selectedService.id}</p>
                                        </div>
                                        <div>
                                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Title (English)</label>
                                            <h3 className="text-xl font-bold text-gray-900">{selectedService.title?.en || '-'}</h3>
                                        </div>
                                        <div>
                                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Title (Arabic)</label>
                                            <h3 className="text-xl font-bold text-gray-900 font-arabic">{selectedService.title?.ar || '-'}</h3>
                                        </div>
                                    </div>
                                </div>

                                {/* Descriptions */}
                                <div className="grid md:grid-cols-2 gap-8 border-t pt-6">
                                    <div className="space-y-4">
                                        <h4 className="font-semibold text-lg text-blue-600 flex items-center gap-2">
                                            <span>🇬🇧</span> English Content
                                        </h4>
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700">Short Description</label>
                                            <p className="text-gray-600 mt-1 text-sm leading-relaxed">{selectedService.short_description?.en || '-'}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700">Long Description</label>
                                            <p className="text-gray-600 mt-1 text-sm leading-relaxed whitespace-pre-wrap">{selectedService.long_description?.en || '-'}</p>
                                        </div>
                                    </div>
                                    <div className="space-y-4">
                                        <h4 className="font-semibold text-lg text-blue-600 flex items-center gap-2 font-arabic">
                                            <span>🇸🇦</span> المحتوى العربي
                                        </h4>
                                        <div dir="rtl">
                                            <label className="text-sm font-semibold text-gray-700 font-arabic">الوصف المختصر</label>
                                            <p className="text-gray-600 mt-1 text-sm leading-relaxed font-arabic">{selectedService.short_description?.ar || '-'}</p>
                                        </div>
                                        <div dir="rtl">
                                            <label className="text-sm font-semibold text-gray-700 font-arabic">الوصف الكامل</label>
                                            <p className="text-gray-600 mt-1 text-sm leading-relaxed whitespace-pre-wrap font-arabic">{selectedService.long_description?.ar || '-'}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Benefits */}
                                {selectedService.benefits && selectedService.benefits.length > 0 && (
                                    <div className="border-t pt-6">
                                        <h4 className="font-semibold text-lg text-gray-900 mb-4">Benefits</h4>
                                        <div className="grid md:grid-cols-2 gap-4">
                                            {selectedService.benefits.map((benefit, idx) => (
                                                <div key={benefit.id || idx} className="bg-gray-50 p-3 rounded-lg flex flex-col gap-1">
                                                    <div className="text-sm text-gray-800">
                                                        <span className="font-medium text-blue-600">EN: </span>
                                                        {benefit.title?.en || '-'}
                                                    </div>
                                                    <div className="text-sm text-gray-800 font-arabic text-right">
                                                        <span className="font-medium text-blue-600">AR: </span>
                                                        {benefit.title?.ar || '-'}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Timestamps */}
                                <div className="border-t pt-6 grid grid-cols-2 gap-4 text-sm text-gray-500">
                                    <div>
                                        <span className="block font-medium">Created At</span>
                                        {new Date(selectedService.created_at).toLocaleString()}
                                    </div>
                                    <div>
                                        <span className="block font-medium">Last Updated</span>
                                        {new Date(selectedService.updated_at).toLocaleString()}
                                    </div>
                                </div>
                            </div>

                            <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4 flex justify-end">
                                <button
                                    onClick={() => setSelectedService(null)}
                                    className="px-6 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition"
                                >
                                    Close
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}
